import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../ui/use-toast';
import type { CompetitionRound } from '../../types/competition-round';

export type ClockRound = Pick<
  CompetitionRound,
  | 'running'
  | 'ended'
  | 'correcting'
  | 'under_review'
  | 'closed'
  | 'seconds_remaining'
  | 'seconds_until_start'
  | 'duration_minutes'
>;

/** Under this many seconds left the clock goes amber: time to finish what is on the wall. */
export const SOON_SECONDS = 5 * 60;
/** And under this, red: the last minute. */
export const URGENT_SECONDS = 60;
/**
 * From this many seconds down the clock counts in minutes and seconds; above it
 * the strip says the time of day it ends or starts at instead. A countdown of
 * hundreds of hours is a number nobody reads.
 */
export const COUNTDOWN_SECONDS = 90 * 60;

/**
 * How the clock reads at a glance: plenty of time, getting short, the last
 * minute — or `idle`, a clock that is not running anything out (the wait for a
 * start, a clock at zero).
 */
export type ClockTone = 'calm' | 'soon' | 'urgent' | 'idle';

export function clockTone(remaining: number, runningOut: boolean): ClockTone {
  if (!runningOut || remaining <= 0) return 'idle';
  if (remaining <= URGENT_SECONDS) return 'urgent';
  if (remaining <= SOON_SECONDS) return 'soon';
  return 'calm';
}

const pad = (n: number) => String(n).padStart(2, '0');

/** h:m:s, every field padded so the width never jumps. */
export function formatHms(totalSeconds: number): string {
  const seconds = Math.max(0, totalSeconds);
  return `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`;
}

/**
 * Minutes and seconds only — the strip's short form. The minutes are all of
 * them, so ninety minutes left reads 90:00 rather than an hour gone missing.
 */
export function formatMinutesSeconds(totalSeconds: number): string {
  const seconds = Math.max(0, totalSeconds);
  return `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`;
}

/**
 * The time of day `secondsFromNow` lands on: just the hour today, the day too
 * otherwise. What the strip says in place of a countdown too long to read.
 */
export function formatClockTime(secondsFromNow: number, locale?: string, now = new Date()): string {
  const at = new Date(now.getTime() + Math.max(0, secondsFromNow) * 1000);
  const time: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  return at.toDateString() === now.toDateString()
    ? at.toLocaleTimeString(locale, time)
    : at.toLocaleString(locale, { weekday: 'short', day: 'numeric', month: 'short', ...time });
}

/**
 * Seconds left on a clock, counted down locally once a second from the figure
 * the server sent — so a device with a wrong clock still counts the right
 * number of seconds — and reset whenever a fetch brings a new figure. Never
 * below zero; `onExpire` once, on reaching it.
 */
export function useCountdown(secondsRemaining: number, onExpire?: () => void): number {
  const [remaining, setRemaining] = useState(secondsRemaining);
  // Kept in a ref so the ticking effect does not restart every time the parent
  // re-renders with a new closure — that would drop a second each time.
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => setRemaining(secondsRemaining), [secondsRemaining]);

  // A plain boolean for the effect: it has to run on the transition into
  // "there is time left" and nowhere else, because re-creating the interval
  // every second would make the clock drift.
  const ticking = remaining > 0;

  useEffect(() => {
    if (!ticking) return;

    const timer = setInterval(() => {
      setRemaining((current) => {
        const next = current - 1;
        if (next <= 0) {
          onExpireRef.current?.();
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [ticking]);

  return remaining;
}

/** Whether the round has a clock to show: the time left, or the time until it begins. */
export function hasRoundClock(round: ClockRound | null): boolean {
  if (!round) return false;
  if (round.running) return typeof round.seconds_remaining === 'number';
  return !round.ended && typeof round.seconds_until_start === 'number';
}

/**
 * What a page does when the clock reaches zero. The server already refuses
 * results from this moment — `CompetitionRound#running?` is derived from the
 * time, not from the stored status — so this only has to make the page agree,
 * without waiting for a refetch to tell it what it can already see.
 */
export function useRoundTimeUp(setRound: Dispatch<SetStateAction<CompetitionRound | null>>) {
  const { t } = useTranslation();
  const { toast } = useToast();

  return useCallback(() => {
    setRound((current) =>
      current
        ? { ...current, running: false, ended: true, correcting: true, seconds_remaining: 0 }
        : current
    );
    toast({ title: t('competitionRounds.timeUpTitle') });
  }, [setRound, t, toast]);
}
